import * as React from "react"
import { EyeIcon, EyeOffIcon, GripVerticalIcon, SlidersHorizontalIcon } from "lucide-react"
import { Reorder, useDragControls } from "motion/react"

import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

export type UsersTableColumnOption = {
  key: string
  label: string
  visible: boolean
}

type UsersTableColumnsPopoverProps = {
  columns: UsersTableColumnOption[]
  onChange: (columns: UsersTableColumnOption[]) => void
  onReset: () => void
}

type ColumnRowProps = {
  column: UsersTableColumnOption
  isLastVisibleColumn: boolean
  isDragging: boolean
  onDragStart: (columnKey: string) => void
  onDragEnd: () => void
  onToggleVisibility: (columnKey: string) => void
}

function ColumnRow({
  column,
  isLastVisibleColumn,
  isDragging,
  onDragStart,
  onDragEnd,
  onToggleVisibility,
}: ColumnRowProps) {
  const dragControls = useDragControls()

  return (
    <Reorder.Item
      value={column}
      id={column.key}
      dragListener={false}
      dragControls={dragControls}
      onDragStart={() => onDragStart(column.key)}
      onDragEnd={onDragEnd}
      whileDrag={{
        scale: 1.01,
        boxShadow: "0 18px 40px rgba(15, 23, 42, 0.14)",
      }}
      transition={{ duration: 0.18 }}
      className={cn(
        "relative flex items-center gap-3 rounded-2xl border bg-card px-3 py-1.5 text-card-foreground",
        isDragging
          ? "z-20 border-foreground/15 bg-muted"
          : "border-border transition-colors hover:border-foreground/20 hover:bg-muted/60"
      )}
    >
      <button
        type="button"
        onPointerDown={(event) => {
          dragControls.start(event)
        }}
        className="inline-flex size-8 cursor-grab items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground active:cursor-grabbing"
        aria-label={`Déplacer la colonne ${column.label}`}
      >
        <GripVerticalIcon className="size-4" />
      </button>

      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{column.label}</p>
      </div>

      <button
        type="button"
        onClick={() => onToggleVisibility(column.key)}
        disabled={column.visible && isLastVisibleColumn}
        className={cn(
          "inline-flex size-8 items-center justify-center rounded-full text-muted-foreground transition-colors",
          column.visible
            ? "hover:bg-muted hover:text-foreground"
            : "text-muted-foreground/50 hover:bg-muted hover:text-muted-foreground",
          column.visible && isLastVisibleColumn
            ? "cursor-not-allowed opacity-40"
            : "cursor-pointer"
        )}
        aria-pressed={column.visible}
        aria-label={column.visible ? `Masquer ${column.label}` : `Afficher ${column.label}`}
      >
        {column.visible ? <EyeIcon className="size-4" /> : <EyeOffIcon className="size-4" />}
      </button>
    </Reorder.Item>
  )
}

export function UsersTableColumnsPopover({
  columns,
  onChange,
  onReset,
}: UsersTableColumnsPopoverProps) {
  const [draggingColumnKey, setDraggingColumnKey] = React.useState<string | null>(null)
  const visibleColumnsCount = columns.filter((column) => column.visible).length

  function toggleVisibility(columnKey: string) {
    onChange(
      columns.map((column) => {
        if (column.key !== columnKey) {
          return column
        }

        if (column.visible && visibleColumnsCount === 1) {
          return column
        }

        return {
          ...column,
          visible: !column.visible,
        }
      })
    )
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="h-12 border-border bg-background px-4 text-[15px] font-medium text-foreground hover:border-foreground/20 hover:bg-muted dark:bg-muted dark:hover:bg-muted/80"
          aria-label="Configurer les colonnes"
        >
          <SlidersHorizontalIcon className="size-4" />
          Colonnes
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[24rem] overflow-hidden rounded-2xl border-0 p-1 ring-1 ring-foreground/5 dark:ring-foreground/10">
        <div className="border-b border-border/50 px-3 py-2.5">
          <p className="text-xs text-muted-foreground">Colonnes du tableau</p>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">
            Affiche ou masque des colonnes, puis réorganise-les par glisser-déposer.
          </p>
        </div>

        <Reorder.Group
          axis="y"
          values={columns}
          onReorder={onChange}
          className="space-y-2 p-2"
          layoutScroll
        >
          {columns.map((column) => (
            <ColumnRow
              key={column.key}
              column={column}
              isLastVisibleColumn={visibleColumnsCount === 1}
              isDragging={draggingColumnKey === column.key}
              onDragStart={setDraggingColumnKey}
              onDragEnd={() => setDraggingColumnKey(null)}
              onToggleVisibility={toggleVisibility}
            />
          ))}
        </Reorder.Group>

        <div className="flex items-center justify-between border-t border-border/50 px-3 py-2.5">
          <p className="text-xs text-muted-foreground">
            {visibleColumnsCount} colonne{visibleColumnsCount > 1 ? "s" : ""} visible{visibleColumnsCount > 1 ? "s" : ""}
          </p>
          <Button variant="ghost" size="sm" onClick={onReset} className="text-muted-foreground hover:text-foreground">
            Réinitialiser
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
